import { AnimatePresence, motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { useState } from 'react';

interface CaseTypeByRepData {
  Name: string; // Salesforce returns this as 'Name' when we query 'Owner.Name'
  Type: string;
  total_count: number;
}

interface CaseTypeByRepChartProps {
  data: CaseTypeByRepData[];
  title: string;
}

// Exact colors from the Case Origin Distribution donut chart
const CASE_TYPE_COLORS: { [key: string]: string } = {
  'General Inquiry': '#60A5FA', // Sky Blue (large segment from donut)
  'System Issue': '#EF4444', // Red
  Subscription: '#A78BFA', // Soft Purple (from donut)
  Billing: '#FBBF24', // Yellow/Gold
  'EHR Billing': '#FB923C', // Orange
  'Feature Request': '#8B5CF6', // Purple
  Training: '#4ADE80', // Green (from donut)
  'Bug Report': '#F472B6', // Pink
  'Data Issue': '#34D399', // Emerald Green
  'Account Issue': '#2DD4BF', // Teal
  Churn: '#DC2626', // Deep Red
  Suggestion: '#C084FC', // Light Purple
  Unknown: '#D1D5DB', // Light Gray
  Other: '#9CA3AF', // Medium Gray
};

// Get color for a case type with fallback
const getCaseTypeColor = (type: string): string => {
  return CASE_TYPE_COLORS[type] || '#9CA3AF'; // Default gray for unknown types
};

interface TooltipData {
  rep: string;
  type: string;
  count: number;
  percentage: number;
  x: number;
  y: number;
}

const CaseTypeByRepChart = ({ data, title }: CaseTypeByRepChartProps) => {
  const [hoveredSegment, setHoveredSegment] = useState<{ rep: string; type: string } | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
        <div className="text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No case type data available by rep</p>
        </div>
      </div>
    );
  }

  // Process data: Group by rep, then by type
  const repData: {
    [rep: string]: { [type: string]: number; total: number };
  } = {};

  data.forEach(item => {
    const rep = item.Name; // Salesforce returns Owner.Name as 'Name' in the result
    const type = item.Type || 'Unknown';
    const count = item.total_count;

    if (!repData[rep]) {
      repData[rep] = { total: 0 };
    }

    repData[rep][type] = count;
    repData[rep].total += count;
  });

  // Get all unique case types across all reps
  const allCaseTypes = Array.from(new Set(data.map(item => item.Type || 'Unknown'))).sort();

  // Sort reps by total cases (descending)
  const sortedReps = Object.keys(repData).sort((a, b) => repData[b].total - repData[a].total);

  // Calculate max total for scaling
  const maxTotal = Math.max(...Object.values(repData).map(r => r.total));

  // Handle bar hover
  const handleSegmentHover = (
    rep: string,
    type: string,
    count: number,
    event: React.MouseEvent<SVGRectElement>
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const repTotal = repData[rep].total;
    const percentage = (count / repTotal) * 100;

    setTooltip({
      rep,
      type,
      count,
      percentage,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
    setHoveredSegment({ rep, type });
  };

  const handleSegmentLeave = () => {
    setTooltip(null);
    setHoveredSegment(null);
  };

  // Chart dimensions
  const chartWidth = 1000;
  const chartHeight = Math.max(400, sortedReps.length * 60); // Dynamic height based on number of reps
  const padding = { top: 80, right: 80, bottom: 60, left: 150 }; // Reduced right padding for fuller bars
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const barHeight = Math.min(40, innerHeight / sortedReps.length - 10);
  const barSpacing = (innerHeight - barHeight * sortedReps.length) / (sortedReps.length + 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/20 rounded-xl shadow-md border border-indigo-200 dark:border-indigo-800/30">
          <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>

      {/* Chart Container */}
      <div className="bg-gradient-to-br from-white via-gray-50/30 to-white dark:from-gray-800 dark:via-gray-800/50 dark:to-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-2xl">
        <div className="overflow-x-auto">
          <svg
            width={chartWidth}
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="min-w-full"
          >
            {/* Gradients for shiny effect */}
            <defs>
              {allCaseTypes.map(type => {
                const color = getCaseTypeColor(type);
                const gradientId = `gradient-${type.replace(/\s+/g, '-')}`;

                return (
                  <linearGradient
                    key={gradientId}
                    id={gradientId}
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="50%" stopColor={color} stopOpacity="0.95" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.85" />
                  </linearGradient>
                );
              })}
            </defs>

            {/* Title */}
            <text
              x={chartWidth / 2}
              y={35}
              textAnchor="middle"
              className="text-lg font-bold fill-gray-700 dark:fill-gray-300"
            >
              Case Type Distribution by Support Agent
            </text>

            {/* Y-axis (Rep names) */}
            {sortedReps.map((rep, index) => {
              const y = padding.top + barSpacing + index * (barHeight + barSpacing) + barHeight / 2;

              return (
                <text
                  key={`rep-label-${index}`}
                  x={padding.left - 10}
                  y={y}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="text-sm font-medium fill-gray-700 dark:fill-gray-300"
                >
                  {rep}
                </text>
              );
            })}

            {/* X-axis */}
            <line
              x1={padding.left}
              y1={chartHeight - padding.bottom}
              x2={chartWidth - padding.right}
              y2={chartHeight - padding.bottom}
              stroke="currentColor"
              strokeWidth="2"
              className="text-gray-300 dark:text-gray-600"
            />

            {/* Bars (Stacked) */}
            {sortedReps.map((rep, repIndex) => {
              const repTotal = repData[rep].total;
              const barWidth = (repTotal / maxTotal) * innerWidth;
              const y = padding.top + barSpacing + repIndex * (barHeight + barSpacing);

              let cumulativeX = padding.left;

              return (
                <g key={`bar-${repIndex}`}>
                  {/* Background bar */}
                  <rect
                    x={padding.left}
                    y={y}
                    width={innerWidth}
                    height={barHeight}
                    fill="currentColor"
                    className="text-gray-100 dark:text-gray-700"
                    rx="8"
                  />

                  {/* Stacked segments */}
                  {allCaseTypes.map((type, typeIndex) => {
                    const count = repData[rep][type] || 0;
                    if (count === 0) return null;

                    const segmentWidth = (count / repTotal) * barWidth;
                    const segmentX = cumulativeX;
                    cumulativeX += segmentWidth;

                    const isHovered = hoveredSegment?.rep === rep && hoveredSegment?.type === type;

                    return (
                      <motion.rect
                        key={`segment-${repIndex}-${typeIndex}`}
                        x={segmentX}
                        y={y}
                        width={segmentWidth}
                        height={barHeight}
                        fill={`url(#gradient-${type.replace(/\s+/g, '-')})`}
                        opacity={isHovered ? 1 : 0.95}
                        rx={
                          typeIndex === 0
                            ? '8 0 0 8'
                            : typeIndex === allCaseTypes.length - 1
                              ? '0 8 8 0'
                              : '0'
                        }
                        initial={{ width: 0 }}
                        animate={{ width: segmentWidth }}
                        transition={{ duration: 0.8, delay: repIndex * 0.1 + typeIndex * 0.05 }}
                        onMouseEnter={e => handleSegmentHover(rep, type, count, e)}
                        onMouseLeave={handleSegmentLeave}
                        className="cursor-pointer transition-all duration-200"
                        style={{
                          filter: isHovered
                            ? 'brightness(1.15) drop-shadow(0 4px 12px rgba(0,0,0,0.25))'
                            : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        }}
                      />
                    );
                  })}

                  {/* Total count label */}
                  <text
                    x={padding.left + barWidth + 10}
                    y={y + barHeight / 2}
                    alignmentBaseline="middle"
                    className="text-sm font-semibold fill-gray-700 dark:fill-gray-300"
                  >
                    {repTotal}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {allCaseTypes.map(type => (
            <div key={type} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getCaseTypeColor(type) }}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      <AnimatePresence>
        {tooltip && (
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              left: tooltip.x,
              top: tooltip.y - 10,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 5 }}
              transition={{ duration: 0.15 }}
              className="bg-gray-900 dark:bg-gray-800 text-white px-4 py-3 rounded-xl shadow-2xl border-2 border-gray-700 dark:border-gray-600"
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: getCaseTypeColor(tooltip.type) }}
                />
                <span className="font-semibold text-sm">{tooltip.type}</span>
              </div>
              <div className="text-xs text-gray-300 mb-1">{tooltip.rep}</div>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-400">{tooltip.count}</div>
                  <div className="text-xs text-gray-400">Cases</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-400">
                    {tooltip.percentage.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-400">of Rep's Total</div>
                </div>
              </div>

              {/* Arrow */}
              <div
                className="absolute top-full left-1/2 transform -translate-x-1/2"
                style={{ marginTop: '-1px' }}
              >
                <div
                  className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent"
                  style={{ borderTopColor: '#374151' }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CaseTypeByRepChart;
